<template>
  <div class="planner-sheet-print">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Planner Grid</h4>
        <b-badge
          v-show="!loading && !errorMessage"
          variant="light-success"
          class="ml-2"
        >
          {{ tableRows.length }} Lessons
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading planner grid...</p>
    </div>

    <div v-else-if="errorMessage" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ errorMessage }}</h4>
    </div>

    <div v-else class="print-page">
      <div v-if="schoolLogo" class="report-brand">
        <img :src="schoolLogo" alt="School logo" class="school-logo" />
      </div>

      <div class="sheet-header">
        <h2 class="sheet-title">{{ reportTitle }}</h2>
        <p v-if="sessionName" class="sheet-meta">Session: {{ sessionName }}</p>
      </div>

      <div v-if="!tableRows.length" class="empty-state">
        No planner details found.
      </div>

      <div v-else class="grid-table-wrap">
        <table class="planner-grid-table">
          <thead>
            <tr>
              <th class="col-concept">Strand/Concept</th>
              <th class="col-topic">Main Topic</th>
              <th class="col-subtopic">Sub Topic</th>
              <th
                v-for="header in plannerHeaders"
                :key="header.id"
                class="col-dynamic"
              >
                {{ header.header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in processedGridRows"
              :key="row.key"
            >
              <td
                v-if="row.showConcept"
                :rowspan="row.conceptRowspan"
                class="cell-group"
                dir="auto"
              >
                {{ row.concept || "—" }}
              </td>
              <td
                v-if="row.showTopic"
                :rowspan="row.topicRowspan"
                class="cell-group"
                dir="auto"
              >
                {{ row.topic || "—" }}
              </td>
              <td class="cell-subtopic" dir="auto">
                <div v-if="row.subtopic" class="cell-text">
                  {{ row.subtopic }}
                </div>
                <span v-else class="muted">—</span>
              </td>
              <td
                v-for="header in plannerHeaders"
                :key="`${row.key}-${header.id}`"
                class="cell-dynamic"
                dir="auto"
              >
                <div
                  v-if="getHeaderValue(row, header.id)"
                  class="cell-text"
                >
                  {{ getHeaderValue(row, header.id) }}
                </div>
                <span v-else class="muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BBadge, BSpinner } from "bootstrap-vue";
import axios from "axios";
import { mapActions } from "vuex";

export default {
  name: "PlannerSheetPrint",
  components: {
    BBadge,
    BSpinner,
  },
  data() {
    return {
      loading: true,
      errorMessage: "",
      sheetId: 0,
      campusId: 0,
      db: "",
      plannerHeaders: [],
      gradeName: "",
      subjectName: "",
      sessionName: "",
      sheetTitle: "",
      schoolName: "My Skool",
      schoolLogo: null,
      gridRows: [],
      originalPageTitle: "",
      autoPrint: false,
    };
  },
  computed: {
    reportTitle() {
      if (this.sheetTitle) return this.sheetTitle;
      const grade = this.gradeName || "—";
      const subject = this.subjectName || "—";
      return `Planner Grid for ${grade} ${subject}`;
    },
    tableRows() {
      return Array.isArray(this.gridRows) ? this.gridRows : [];
    },
    processedGridRows() {
      const rows = this.tableRows;
      return rows.map((row, index) => {
        let conceptRowspan = 1;
        let topicRowspan = 1;
        let showConcept = true;
        let showTopic = true;

        if (
          index > 0 &&
          String(rows[index - 1].concept) === String(row.concept)
        ) {
          showConcept = false;
        } else {
          for (let j = index + 1; j < rows.length; j++) {
            if (String(rows[j].concept) === String(row.concept)) {
              conceptRowspan++;
            } else {
              break;
            }
          }
        }

        if (
          index > 0 &&
          String(rows[index - 1].concept) === String(row.concept) &&
          String(rows[index - 1].topic) === String(row.topic)
        ) {
          showTopic = false;
        } else {
          for (let j = index + 1; j < rows.length; j++) {
            if (
              String(rows[j].concept) === String(row.concept) &&
              String(rows[j].topic) === String(row.topic)
            ) {
              topicRowspan++;
            } else {
              break;
            }
          }
        }

        return {
          ...row,
          showConcept,
          conceptRowspan,
          showTopic,
          topicRowspan,
        };
      });
    },
  },
  async created() {
    this.originalPageTitle = document.title;
    document.title = " ";
    await this.initReport();
  },
  beforeDestroy() {
    if (this.originalPageTitle) {
      document.title = this.originalPageTitle;
    }
  },
  mounted() {
    if (this.autoPrint) {
      this.$nextTick(() => {
        setTimeout(() => this.print(), 500);
      });
    }
  },
  methods: {
    ...mapActions({ get: "get" }),

    async initReport() {
      const query = this.$route.query || {};
      this.sheetId = Number(query.id) || 0;
      this.db = query.db || this.$store.state.userData.db;
      this.campusId = Number(query.cID || this.$store.state.userData.cId);
      this.autoPrint = query.autoPrint === "1";
      this.gradeName = query.className || "";
      this.subjectName = query.subjectName || "";
      this.sessionName = query.sessionName || "";
      this.schoolName =
        query.campusName ||
        this.$store.state.userData?.campusName ||
        this.$store.state.userData?.organizationName ||
        "My Skool";

      if (!this.sheetId) {
        this.errorMessage = "Planner sheet ID is missing.";
        this.loading = false;
        return;
      }

      try {
        await Promise.all([this.loadCampusInfo(), this.loadReport()]);
      } catch (error) {
        console.error("Planner sheet print error:", error);
        this.errorMessage = "Failed to load planner sheet report.";
      } finally {
        this.loading = false;
      }
    },

    async loadCampusInfo() {
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db: this.db, cId: this.campusId },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find(
            (campus) => Number(campus.id) === Number(this.campusId)
          );
          if (match) {
            this.schoolLogo = match.logo || null;
          }
        }
      } catch (error) {
        console.warn("Campus info fetch failed for planner sheet print", error);
      }
    },

    async loadReport() {
      const response = await this.get({
        url: `${this.$store.state.domain}PlannerSheet/GetPlannerSheetReport/${this.campusId}?db=${this.db}&plannerSheetID=${this.sheetId}`,
        token: this.$store.state.userData.token,
      });

      if (!response) {
        throw new Error("Empty planner sheet report response");
      }

      const sheet = response.sheet || {};
      this.sheetTitle = sheet.title || "";
      this.gradeName = sheet.className || this.gradeName;
      this.subjectName = sheet.subjectName || this.subjectName;
      this.sessionName = sheet.sessionName || this.sessionName;

      this.plannerHeaders = (response.headers || []).map((header, index) => ({
        id: header.id != null ? header.id : `hdr-${index}`,
        header: header.header || header.name || `Header ${header.id || index}`,
      }));

      this.gridRows = (response.rows || [])
        .map((row, index) => {
          const headers = {};
          this.plannerHeaders.forEach((header) => {
            headers[header.id] = "";
          });

          (row.columns || []).forEach((column) => {
            const matchedHeader = this.plannerHeaders.find(
              (header) =>
                header.id == column.headerID ||
                String(header.header || "").trim() ===
                  String(column.header || "").trim()
            );
            const key = matchedHeader
              ? matchedHeader.id
              : column.headerID || column.header;
            if (key != null && key !== "") {
              headers[key] = this.processContentForDisplay(column.remarks || "");
            }
          });

          return {
            key: `row-${index}`,
            concept: this.cleanConceptText(row.concept || ""),
            topic: row.mainTopic || "",
            subtopic: row.subTopic || row.subtopic || "",
            headers,
          };
        })
        .filter(
          (row) =>
            row.concept ||
            row.topic ||
            row.subtopic ||
            Object.values(row.headers || {}).some((value) =>
              String(value || "").trim()
            )
        );
    },

    getHeaderValue(row, headerId) {
      if (!row || !row.headers) return "";
      if (row.headers[headerId] != null) return row.headers[headerId];
      const matchKey = Object.keys(row.headers).find((k) => k == headerId);
      return matchKey != null ? row.headers[matchKey] : "";
    },

    cleanConceptText(text) {
      if (!text) return "";
      return String(text)
        .replace(/^(VIII|IX|VII|VI|V|IV|III|II|I)\s*-\s*/i, "")
        .replace(/^(Class\s+\d+|Grade\s+\d+)\s*-\s*/i, "")
        .replace(/^(\d+)\s*-\s*/, "")
        .trim();
    },

    stripHtmlTags(text) {
      if (!text) return "";
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = text;
      return (tempDiv.textContent || tempDiv.innerText || "").trim();
    },

    processContentForDisplay(content) {
      if (!content) return "";
      const value = String(content).trim();
      if (!value.includes("<")) return value;
      return this.stripHtmlTags(value);
    },

    print() {
      const previousTitle = document.title;
      document.title = " ";
      const restoreTitle = () => {
        document.title = this.originalPageTitle || previousTitle;
        window.removeEventListener("afterprint", restoreTitle);
      };
      window.addEventListener("afterprint", restoreTitle);
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.planner-sheet-print {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2rem;
  font-family: "Segoe UI", Arial, sans-serif;
  color: #111;
  box-sizing: border-box;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1rem 1.75rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.2rem;
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    color: #e74c3c;
    border-color: #fab1a0;
  }
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #666;
}

.print-page {
  background: #fff;
  padding: 1.5rem 1.25rem 2rem;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.report-brand {
  margin-bottom: 0.75rem;
}

.school-logo {
  height: 52px;
  width: auto;
  object-fit: contain;
  display: block;
}

.sheet-header {
  margin-bottom: 1rem;
}

.sheet-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #111;
}

.sheet-meta {
  margin: 0.35rem 0 0;
  font-size: 0.88rem;
  color: #555;
}

.grid-table-wrap {
  overflow-x: auto;
}

.planner-grid-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 0.78rem;
  line-height: 1.4;
}

.planner-grid-table th,
.planner-grid-table td {
  border: 1px solid #111;
  vertical-align: top;
  padding: 0.45rem 0.5rem;
  word-break: break-word;
}

.planner-grid-table th {
  background: #f3f4f6;
  font-weight: 800;
  text-align: center;
  font-size: 0.72rem;
}

.col-concept {
  width: 8%;
}

.col-topic {
  width: 10%;
}

.col-subtopic {
  width: 12%;
}

.col-dynamic {
  width: auto;
}

.cell-group {
  font-weight: 600;
  background: #fff;
}

.cell-subtopic,
.cell-dynamic {
  background: #fff;
}

.cell-text {
  margin: 0;
  white-space: pre-line;
  line-height: 1.45;
}

.muted {
  color: #9ca3af;
}

@media (max-width: 767.98px) {
  .planner-sheet-print {
    padding: 1rem;
  }

  .print-page {
    padding: 1rem 0.75rem 1.25rem;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .planner-sheet-print {
    background: #fff !important;
    padding: 0 !important;
    min-height: 0 !important;
  }

  .print-page {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .sheet-header {
    margin-bottom: 0.5rem !important;
    page-break-after: avoid;
  }

  .sheet-title {
    font-size: 11pt !important;
  }

  .planner-grid-table {
    font-size: 7.5pt !important;
  }

  .planner-grid-table th,
  .planner-grid-table td {
    padding: 0.25rem 0.35rem !important;
  }

  .planner-grid-table th {
    background: #f3f4f6 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .planner-grid-table tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }
}
</style>

<style lang="scss">
@page {
  size: A4 landscape;
  margin: 8mm;
}

@media print {
  html,
  body {
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    overflow: visible !important;
  }

  #app,
  .content-wrapper,
  .content-area-wrapper,
  .container,
  .router-view,
  .layout-full {
    min-height: 0 !important;
    height: auto !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }
}
</style>
